import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { resolveNewsApiLang } from '@formosa/shared/utils/newsLang';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui';
import NotificationCenterTabs from '../notification-center/NotificationCenterTabs';
import './member-content-notices.css';

const PAGE_SIZE = 20;

/**
 * PD-NTF-01 Wave 3 — 站內信分入口。
 * 讀取 Member inbox API；不合併進 Center unread、不加總 badge。
 */
export default function SiteMailPage() {
  const { locale } = useTravelTranslation();
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const generation = useRef(0);

  const load = useCallback(async (pageNo) => {
    const token = ++generation.current;
    setPhase('loading');
    setError('');
    try {
      const data = await memberApi.inboxPageQuery({
        pageNo,
        pageSize: PAGE_SIZE,
        lang: resolveNewsApiLang(locale),
      });
      if (token !== generation.current) return;
      setRecords(data?.records || []);
      setTotalPages(data?.pages || 1);
      setPage(pageNo);
      setPhase('ready');
    } catch (cause) {
      if (token !== generation.current) return;
      setError(cause?.message || '無法載入站內信。');
      setRecords([]);
      setPhase('error');
    }
  }, [locale]);

  useEffect(() => {
    load(1);
    return () => {
      generation.current += 1;
    };
  }, [load]);

  return (
    <main className="member-content-notices" data-testid="site-mail-page">
      <NotificationCenterTabs active="siteMail" />
      <header className="member-content-notices__header">
        <h1>站內信</h1>
        <p>來自會員中心的站內信，與社群／揪團通知分開存放。</p>
      </header>

      {phase === 'loading' ? <LoadingState label="載入站內信…" /> : null}
      {phase === 'error' ? (
        <ErrorState
          title="無法載入站內信"
          description={error}
          retryable
          retryLabel="再試一次"
          onRetry={() => load(page)}
        />
      ) : null}
      {phase === 'ready' && records.length === 0 ? (
        <EmptyState title="目前沒有站內信" description="有新站內信時會顯示在這裡。" />
      ) : null}
      {phase === 'ready' && records.length > 0 ? (
        <ul className="member-content-notices__list">
          {records.map((item) => {
            const id = item.id ?? item.inboxId;
            const unread = item.status === 0 || item.readStatus === 0 || item.unread === true;
            return (
              <li key={String(id)}>
                <Link
                  to={`/site-mail/${id}`}
                  className={`member-content-notices__row${unread ? ' is-unread' : ''}`}
                  data-testid={`site-mail-row-${id}`}
                >
                  <strong>{item.title || '站內信'}</strong>
                  {item.createTime ? (
                    <time dateTime={item.createTime}>{formatDateTime(item.createTime)}</time>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
      {phase === 'ready' && totalPages > 1 ? (
        <div className="member-content-notices__pager">
          <button type="button" disabled={page <= 1} onClick={() => load(page - 1)}>
            上一頁
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button type="button" disabled={page >= totalPages} onClick={() => load(page + 1)}>
            下一頁
          </button>
        </div>
      ) : null}
    </main>
  );
}
